import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Layout from "./components/layout/Layout";
import Home from "./pages/site/Home";
import { ServiceGate } from "./components/site/SiteBlocks";
import { LANGS, SERVICES, STATIC_PATHS, servicePath } from "./lib/services";

const ServiceOverview = lazy(() => import("./pages/site/ServiceOverview"));
const ServiceProvince = lazy(() => import("./pages/site/ServiceProvince"));
const ServicePlace = lazy(() => import("./pages/site/ServicePlace"));
const AdminServices = lazy(() => import("./pages/admin/AdminServices"));
const AdminWebsite = lazy(() => import("./pages/admin/AdminWebsite"));

const hw = (l: "nl" | "en" | "fr") => servicePath("security-hardware-software", l);
const cyber = (l: "nl" | "en" | "fr") => servicePath("cybersecurity", l);
const LEGACY_REDIRECTS: [string, string][] = [
  ["/producten", hw("nl")], ["/producten/*", hw("nl")],
  ["/products", hw("en")], ["/products/*", hw("en")],
  ["/produits", hw("fr")], ["/produits/*", hw("fr")],
  ["/trainingen", cyber("nl")], ["/trainingen/*", cyber("nl")],
  ["/trainings", cyber("en")], ["/trainings/*", cyber("en")],
  ["/formations", cyber("fr")], ["/formations/*", cyber("fr")],
  ["/oplossingen", cyber("nl")], ["/oplossingen/*", cyber("nl")],
  ["/solutions", cyber("en")], ["/solutions/*", cyber("en")],
  ["/solutions-fr", cyber("fr")], ["/solutions-fr/*", cyber("fr")],
  ["/partner-worden", STATIC_PATHS.contact.nl], ["/partner-registration", STATIC_PATHS.contact.en],
  ["/inscription-partenaire", STATIC_PATHS.contact.fr],
  ["/about", STATIC_PATHS.about.en], ["/a-propos", STATIC_PATHS.about.fr],
  ["/news", "/nieuws"], ["/actualites", "/nieuws"],
];
import CookieConsent from "./components/CookieConsent";
import PageTracker from "./components/PageTracker";

// Lazy load all pages except Index for code splitting
const About = lazy(() => import("./pages/About"));
const News = lazy(() => import("./pages/News"));
const Contact = lazy(() => import("./pages/Contact"));
const FirewallBrands = lazy(() => import("./pages/FirewallBrands"));
const FirewallBrandPage = lazy(() => import("./pages/FirewallBrandPage"));
const FirewallProvincePage = lazy(() => import("./pages/FirewallProvincePage"));
const FirewallCityPage = lazy(() => import("./pages/FirewallCityPage"));
const FirewallNeighborhoodPage = lazy(() => import("./pages/FirewallNeighborhoodPage"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Terms = lazy(() => import("./pages/Terms"));
const CookiePreferences = lazy(() => import("./pages/CookiePreferences"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const Sitemap = lazy(() => import("./pages/Sitemap"));

const CustomerRegistration = lazy(() => import("./pages/CustomerRegistration"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Lazy load admin pages
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminPartners = lazy(() => import("./pages/admin/AdminPartners"));
const AdminArticles = lazy(() => import("./pages/admin/AdminArticles"));
const AdminMessages = lazy(() => import("./pages/admin/AdminMessages"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminSeo = lazy(() => import("./pages/admin/AdminSeo"));
const AdminSeoPages = lazy(() => import("./pages/admin/AdminSeoPages"));
const AdminLocatieBeheer = lazy(() => import("./pages/admin/AdminLocatieBeheer"));
const AdminSitemap = lazy(() => import("./pages/admin/AdminSitemap"));
const AdminContent = lazy(() => import("./pages/admin/AdminContent"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminNavigation = lazy(() => import("./pages/admin/AdminNavigation"));
const AdminTrainings = lazy(() => import("./pages/admin/AdminTrainings"));
const AdminTrainingRegistrations = lazy(() => import("./pages/admin/AdminTrainingRegistrations"));
const AdminApiKeys = lazy(() => import("./pages/admin/AdminApiKeys"));
const AdminStatistics = lazy(() => import("./pages/admin/AdminStatistics"));
const AdminCookies = lazy(() => import("./pages/admin/AdminCookies"));
const AdminSeoAudit = lazy(() => import("./pages/admin/AdminSeoAudit"));
const AdminSeoCoverage = lazy(() => import("./pages/admin/AdminSeoCoverage"));
const AdminSeoIndex = lazy(() => import("./pages/admin/AdminSeoIndex"));
const AdminBrandGuidelines = lazy(() => import("./pages/admin/AdminBrandGuidelines"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminAuditLog = lazy(() => import("./pages/admin/AdminAuditLog"));
const AdminContentVersions = lazy(() => import("./pages/admin/AdminContentVersions"));
const AdminMFA = lazy(() => import("./pages/admin/AdminMFA"));
const AdminTasks = lazy(() => import("./pages/admin/AdminTasks"));
const AdminLeads = lazy(() => import("./pages/admin/AdminLeads"));
const AdminEmail = lazy(() => import("./pages/admin/AdminEmail"));
const AdminSocialMedia = lazy(() => import("./pages/admin/AdminSocialMedia"));
const AdminContentAssistent = lazy(() => import("./pages/admin/AdminContentAssistent"));
const AdminSitemapHosting = lazy(() => import("./pages/admin/AdminSitemapHosting"));
const AdminCustomerRegistrations = lazy(() => import("./pages/admin/AdminCustomerRegistrations"));
const AdminDatabase = lazy(() => import("./pages/admin/AdminDatabase"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AdminRoute = lazy(() => import("./components/admin/AdminRoute"));

import { AdminProvider } from "./contexts/AdminContext";

const queryClient = new QueryClient();

// Simple loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>

            {/* Public site */}
            <Route element={<Layout />}>
              {LANGS.map((lang) => (
                <Route key={`home-${lang}`} path={STATIC_PATHS.home[lang]} element={<Home lang={lang} />} />
              ))}
              {LANGS.flatMap((lang) =>
                SERVICES.flatMap((svc) => {
                  const base = servicePath(svc.key, lang);
                  return [
                    <Route key={`${lang}-${svc.key}`} path={base} element={<ServiceGate serviceKey={svc.key} fallback={<NotFound />}><ServiceOverview lang={lang} serviceKey={svc.key} /></ServiceGate>} />,
                    <Route key={`${lang}-${svc.key}-p`} path={`${base}/:provincieSlug`} element={<ServiceGate serviceKey={svc.key} fallback={<NotFound />}><ServiceProvince lang={lang} serviceKey={svc.key} /></ServiceGate>} />,
                    <Route key={`${lang}-${svc.key}-c`} path={`${base}/:provincieSlug/:stadSlug`} element={<ServiceGate serviceKey={svc.key} fallback={<NotFound />}><ServicePlace lang={lang} serviceKey={svc.key} level="city" /></ServiceGate>} />,
                    <Route key={`${lang}-${svc.key}-w`} path={`${base}/:provincieSlug/:stadSlug/:wijkSlug`} element={<ServiceGate serviceKey={svc.key} fallback={<NotFound />}><ServicePlace lang={lang} serviceKey={svc.key} level="wijk" /></ServiceGate>} />,
                  ];
                })
              )}

              <Route path="/over-ons" element={<About />} />
              <Route path="/en/about" element={<About />} />
              <Route path="/fr/a-propos" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/en/contact" element={<Contact />} />
              <Route path="/fr/contact" element={<Contact />} />
              <Route path="/nieuws" element={<News />} />

              {/* Existing firewall brand pages stay available */}
              <Route path="/firewalls" element={<FirewallBrands />} />
              <Route path="/firewalls/:brandSlug" element={<FirewallBrandPage />} />
              <Route path="/firewalls/:brandSlug/:provincieSlug" element={<FirewallProvincePage />} />
              <Route path="/firewalls/:brandSlug/:provincieSlug/:stadSlug" element={<FirewallCityPage />} />
              <Route path="/firewalls/:brandSlug/:provincieSlug/:stadSlug/:wijkSlug" element={<FirewallNeighborhoodPage />} />
              <Route path="/en/firewalls" element={<FirewallBrands />} />
              <Route path="/en/firewalls/:brandSlug" element={<FirewallBrandPage />} />
              <Route path="/en/firewalls/:brandSlug/:provincieSlug" element={<FirewallProvincePage />} />
              <Route path="/en/firewalls/:brandSlug/:provincieSlug/:stadSlug" element={<FirewallCityPage />} />
              <Route path="/en/firewalls/:brandSlug/:provincieSlug/:stadSlug/:wijkSlug" element={<FirewallNeighborhoodPage />} />
              <Route path="/pare-feux" element={<FirewallBrands />} />
              <Route path="/pare-feux/:brandSlug" element={<FirewallBrandPage />} />
              <Route path="/pare-feux/:brandSlug/:provincieSlug" element={<FirewallProvincePage />} />
              <Route path="/pare-feux/:brandSlug/:provincieSlug/:stadSlug" element={<FirewallCityPage />} />
              <Route path="/pare-feux/:brandSlug/:provincieSlug/:stadSlug/:wijkSlug" element={<FirewallNeighborhoodPage />} />

              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/voorwaarden" element={<Terms />} />
              <Route path="/cookie-voorkeuren" element={<CookiePreferences />} />
              <Route path="/cookiebeleid" element={<CookiePolicy />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/cookie-preferences" element={<CookiePreferences />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/politique-confidentialite" element={<PrivacyPolicy />} />
              <Route path="/conditions-generales" element={<Terms />} />
              <Route path="/sitemap" element={<Sitemap />} />

              {/* Old pages forward to the matching new page */}
              {LEGACY_REDIRECTS.map(([from, to]) => (
                <Route key={from} path={from} element={<Navigate to={to} replace />} />
              ))}
            </Route>

            {/* Standalone pages (with own header/footer) */}
            <Route path="/klant-registratie" element={<CustomerRegistration />} />

            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin/*"
              element={
                <AdminProvider>
                  <AdminRoute>
                    <AdminLayout />
                  </AdminRoute>
                </AdminProvider>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="partners" element={<AdminPartners />} />
              <Route path="artikelen" element={<AdminArticles />} />
              <Route path="berichten" element={<AdminMessages />} />
              <Route path="instellingen" element={<AdminSettings />} />
              <Route path="seo" element={<AdminSeo />} />
              <Route path="seo-paginas" element={<AdminSeoPages />} />
              <Route path="locatiebeheer" element={<AdminLocatieBeheer />} />
              <Route path="seo-audit" element={<AdminSeoAudit />} />
              <Route path="seo-dekking" element={<AdminSeoCoverage />} />
              <Route path="seo-index" element={<AdminSeoIndex />} />
              <Route path="sitemap" element={<AdminSitemap />} />
              <Route path="content" element={<AdminContent />} />
              <Route path="producten" element={<AdminProducts />} />
              <Route path="navigatie" element={<AdminNavigation />} />
              <Route path="trainingen" element={<AdminTrainings />} />
              <Route path="trainingen-aanmeldingen" element={<AdminTrainingRegistrations />} />
              <Route path="api-keys" element={<AdminApiKeys />} />
              <Route path="statistieken" element={<AdminStatistics />} />
              <Route path="cookies" element={<AdminCookies />} />
              <Route path="brand-guidelines" element={<AdminBrandGuidelines />} />
              <Route path="gebruikers" element={<AdminUsers />} />
              <Route path="audit-log" element={<AdminAuditLog />} />
              <Route path="versies" element={<AdminContentVersions />} />
              <Route path="beveiliging" element={<AdminMFA />} />
              <Route path="taken" element={<AdminTasks />} />
              <Route path="leads" element={<AdminLeads />} />
              <Route path="email" element={<AdminEmail />} />
              <Route path="social-media" element={<AdminSocialMedia />} />
              <Route path="content-assistent" element={<AdminContentAssistent />} />
              <Route path="sitemap-hosting" element={<AdminSitemapHosting />} />
              <Route path="klantregistraties" element={<AdminCustomerRegistrations />} />
              <Route path="database" element={<AdminDatabase />} />
              <Route path="diensten" element={<AdminServices />} />
              <Route path="websitebeheer" element={<AdminWebsite />} />
            </Route>

            {/* Catch-all */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <PageTracker />
        <CookieConsent />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
